import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import './Improvements.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function Improvements() {
  const navigate = useNavigate();

  // Upload state
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [uploadedMetadata, setUploadedMetadata] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Analysis state
  const [analyzing, setAnalyzing] = useState(false);
  const [textMetadata, setTextMetadata] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);

  // Improvement state
  const [textEdits, setTextEdits] = useState({});
  const [freeformInstructions, setFreeformInstructions] = useState('');
  const [brandName, setBrandName] = useState('');
  const [notes, setNotes] = useState('');
  const [improving, setImproving] = useState(false);
  const [improvementError, setImprovementError] = useState(null);

  // Success state
  const [improvementSuccess, setImprovementSuccess] = useState(false);
  const [improvedImageUrl, setImprovedImageUrl] = useState(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a JPEG, PNG, or WebP image');
      e.target.value = '';
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      e.target.value = '';
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${API_URL}/standalone-improvements/upload`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setUploadedImageUrl(data.imageUrl);
        setUploadedMetadata({
          ...data.metadata,
          gcsPath: data.gcsPath
        });
        // Auto-analyze after upload
        analyzeImage(data.imageUrl);
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (error) {
      alert(`Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const analyzeImage = async (imageUrl) => {
    setAnalyzing(true);
    setAnalysisError(null);
    setTextMetadata(null);

    try {
      const response = await fetch(`${API_URL}/standalone-improvements/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageUrl })
      });

      const data = await response.json();

      if (data.success) {
        setTextMetadata({
          textElements: data.data?.textElements || data.textElements || [],
          tokenUsage: data.data?.tokenUsage || data.tokenUsage || {}
        });
      } else {
        throw new Error(data.error || 'Analysis failed');
      }
    } catch (error) {
      setAnalysisError(error.message);
      // Still allow freeform improvements
      setTextMetadata({ textElements: [], tokenUsage: {} });
    } finally {
      setAnalyzing(false);
    }
  };

  const handleApplyImprovements = async () => {
    setImproving(true);
    setImprovementError(null);
    setImprovementSuccess(false);

    try {
      // Build text changes
      const textChanges = [];
      if (textMetadata && textMetadata.textElements) {
        textMetadata.textElements.forEach((element, index) => {
          const newText = textEdits[index];
          if (newText !== undefined && newText !== element.text) {
            textChanges.push({
              originalText: element.text,
              newText: newText
            });
          }
        });
      }

      // Validate
      if (textChanges.length === 0 && !freeformInstructions.trim()) {
        setImprovementError('Please make some changes before applying');
        setImproving(false);
        return;
      }

      const response = await fetch(`${API_URL}/standalone-improvements/improve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          originalImageUrl: uploadedImageUrl,
          originalMetadata: uploadedMetadata,
          textChanges,
          freeformInstructions,
          detectedTextElements: textMetadata.textElements,
          analysisTokenUsage: textMetadata.tokenUsage,
          brandName: brandName.trim() || null,
          notes: notes.trim() || null
        })
      });

      const data = await response.json();

      if (data.success) {
        setImprovementSuccess(true);
        setImprovedImageUrl(data.improvement.improvedImageUrl);
      } else {
        throw new Error(data.error || 'Improvement failed');
      }
    } catch (error) {
      setImprovementError(error.message);
    } finally {
      setImproving(false);
    }
  };

  const handleReset = () => {
    setUploadedImageUrl(null);
    setUploadedMetadata(null);
    setTextMetadata(null);
    setAnalysisError(null);
    setImprovementSuccess(false);
    setImprovedImageUrl(null);
    setTextEdits({});
    setFreeformInstructions('');
    setBrandName('');
    setNotes('');
  };

  return (
    <div className="creative-brief-container">
      <div className="flex relative">
        <Sidebar />

        <main className="bg-white flex-1 px-3 pt-10 pb-3">
          <div className="main-layout p-5 py-10">
            <div className="w-full md:w-[75%] lg:w-[65%] mx-auto shadow bg-white rounded-xl p-6 px-6">

              <h2 className="form-section-title">Improvements</h2>
              <p className="form-subtitle">Upload any image and improve it with AI</p>

              {/* Upload Section */}
              {!uploadedImageUrl && (
                <div className="upload-section">
                  <div className="drag-drop-zone" onClick={() => document.getElementById('image-upload').click()}>
                    {uploading ? (
                      <div className="upload-loading">
                        <div className="spinner"></div>
                        <p>Uploading...</p>
                      </div>
                    ) : (
                      <>
                        <span className="upload-icon">📁</span>
                        <p className="upload-text">Click to upload or drag & drop</p>
                        <small className="upload-hint">JPEG, PNG, or WebP (max 10MB)</small>
                      </>
                    )}
                  </div>
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                </div>
              )}

              {/* Preview & Analysis */}
              {uploadedImageUrl && !improvementSuccess && (
                <div className="uploaded-preview">
                  <h3>Uploaded Image</h3>
                  <img src={uploadedImageUrl} alt="Uploaded" className="preview-image" />
                  <div className="preview-meta">
                    <span>{uploadedMetadata.width} × {uploadedMetadata.height}</span>
                    <span>{(uploadedMetadata.size / 1024).toFixed(2)} KB</span>
                  </div>

                  {analyzing && (
                    <div className="analyzing-state">
                      <div className="spinner"></div>
                      <p>Analyzing image for text...</p>
                    </div>
                  )}

                  {!analyzing && textMetadata && (
                    <>
                      {/* Inline Edit Section */}
                      <div className="inline-edit-section">
                        {/* Analysis Error */}
                        {analysisError && (
                          <div className="analysis-error">
                            ⚠️ {analysisError}
                            <p className="error-hint">You can still use freeform instructions below.</p>
                          </div>
                        )}

                        {/* Text Editors */}
                        {textMetadata.textElements.length > 0 && (
                          <div className="text-editors-section">
                            <h4>Edit Text Elements:</h4>
                            {textMetadata.textElements.map((element, index) => (
                              <div key={index} className="text-editor-row">
                                <div className="text-editor-labels">
                                  <span className="text-position-badge">{element.position}</span>
                                  <span className="text-type-badge">{element.type}</span>
                                  <span className="text-size-badge">{element.size}</span>
                                </div>
                                <input
                                  type="text"
                                  value={textEdits[index] !== undefined ? textEdits[index] : element.text}
                                  onChange={(e) => setTextEdits(prev => ({ ...prev, [index]: e.target.value }))}
                                  placeholder={element.text}
                                  className="text-editor-input"
                                  disabled={improving}
                                />
                              </div>
                            ))}
                          </div>
                        )}

                        {/* No text found */}
                        {textMetadata.textElements.length === 0 && (
                          <div className="no-text-found">
                            ℹ️ No text detected automatically. Use freeform instructions below to describe your changes.
                          </div>
                        )}

                        {/* Freeform Instructions */}
                        <div className="freeform-section">
                          <h4>Additional Instructions (Optional):</h4>
                          <textarea
                            value={freeformInstructions}
                            onChange={(e) => setFreeformInstructions(e.target.value)}
                            placeholder="e.g., change background to blue, make logo bigger, remove watermark..."
                            className="freeform-textarea"
                            rows="4"
                            disabled={improving}
                          />
                          <p className="freeform-hint">Describe any changes: remove elements, change colors, adjust positioning, etc.</p>
                        </div>

                        {/* Optional Metadata */}
                        <div className="optional-metadata">
                          <input
                            type="text"
                            value={brandName}
                            onChange={(e) => setBrandName(e.target.value)}
                            placeholder="Brand name (optional)"
                            className="metadata-input"
                            disabled={improving}
                          />
                          <input
                            type="text"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Notes (optional)"
                            className="metadata-input"
                            disabled={improving}
                          />
                        </div>

                        {/* Error */}
                        {improvementError && (
                          <div className="improvement-error">❌ {improvementError}</div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="button-group">
                        <button className="primary-button" onClick={handleApplyImprovements} disabled={improving}>
                          {improving ? (
                            <>
                              <span className="spinner-small"></span>
                              Improving...
                            </>
                          ) : (
                            '🎨 Apply Improvements'
                          )}
                        </button>
                        <button className="secondary-button" onClick={handleReset} disabled={improving}>
                          Upload Different Image
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Success State */}
              {improvementSuccess && improvedImageUrl && (
                <div className="improvement-success-view">
                  <h3>✅ Improvement Complete!</h3>
                  <div className="before-after">
                    <div className="before-section">
                      <h4>Original</h4>
                      <img src={uploadedImageUrl} alt="Original" />
                    </div>
                    <div className="arrow-divider">→</div>
                    <div className="after-section">
                      <h4>Improved</h4>
                      <img src={improvedImageUrl} alt="Improved" />
                    </div>
                  </div>
                  <div className="button-group">
                    <button className="primary-button" onClick={() => navigate('/creative-history')}>
                      📋 View in History
                    </button>
                    <button className="secondary-button" onClick={handleReset}>
                      🔄 Improve Another Image
                    </button>
                  </div>
                </div>
              )}

            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default Improvements;
